// src/lib/tokenisedPaths.js
// =============================================================================
// Routes that carry a bearer credential IN THE PATH.
// =============================================================================
//
// Several pages are authorised by a token in the URL rather than by a login:
// /sign/<token>, /sign-document/<token>, /pay/<token>, /pay-confirm/<token>,
// /portal/<token>. On those pages the token is the ENTIRE authorisation. It is
// what lets somebody with no Sift account read and sign a document, or authorise
// a payment.
//
// That makes the URL a secret, and anything that records or transmits the URL is
// handling a credential:
//
//   * the first-party tracker persists `path` verbatim into
//     analytics_analyticsevent, so a token would sit in a table (and every
//     backup of it) that plenty of people can read;
//   * GA4, GTM, the Meta pixel and the TikTok pixel all read window.location
//     themselves on page load, so the token would leave the platform entirely
//     and land in four third-party consoles we do not control.
//
// Either one is enough for somebody to sign a document as another party, or
// authorise somebody else's payment. Measured on 2026-09-26 before this landed:
// zero stored analytics rows had leaked a token, so this is prevention rather
// than cleanup.
//
// This list lives in its own module because both tracker.js and analytics.js
// need it and tracker.js already imports analytics.js -- importing the other way
// would be a cycle. One list, two consumers, no duplication to drift.

/** Route prefixes whose next path segment is a secret. */
export const TOKENISED_PREFIXES = [
  "sign",
  "sign-document",
  "pay",
  "pay-confirm",
  "portal",
  "invite",
  "verify-email",
  "reset-password",
];

/** True when this path carries a credential in it. */
export function isTokenisedPath(path) {
  if (!path || typeof path !== "string") return false;
  const parts = path.split("?")[0].split("/");
  // Leading slash makes parts[0] empty, so the route name is parts[1] and the
  // token is parts[2]. A bare "/sign" with no token is not a secret.
  return parts.length > 2 && TOKENISED_PREFIXES.includes(parts[1]) && Boolean(parts[2]);
}

/**
 * Pages whose query string carries a visitor's personal details. Calendly's
 * "pass event details" redirect appends invitee_email, invitee_full_name and the
 * booking answers to /calendly-confirmation, and the third-party tags read the
 * whole URL on load, so they would ship that to four ad consoles (RT-X13). The
 * first-party tracker records the path only, so it may still run here.
 */
export const PERSONAL_QUERY_PATHS = ["/calendly-confirmation"];

/** True when the third-party tags must not load on this path. */
export function blocksThirdPartyScripts(path) {
  if (isTokenisedPath(path)) return true;
  if (!path || typeof path !== "string") return false;
  const bare = path.split("?")[0].replace(/\/+$/, "");
  return PERSONAL_QUERY_PATHS.includes(bare);
}

/**
 * The path with any credential removed, safe to record.
 * "/sign-document/abc.def" -> "/sign-document/[redacted]"
 */
export function redactPath(path) {
  if (!path || typeof path !== "string") return path || "";
  const bare = path.split("?")[0];
  if (!isTokenisedPath(bare)) return bare;
  const parts = bare.split("/");
  return `/${parts[1]}/[redacted]`;
}
